import { useEffect } from "react";
import { useLang, EN } from "@/lib/i18n";
import { EX_PATTERNS } from "@/lib/i18n_ex";

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const PATTERNS = Object.entries(EX_PATTERNS).map(([k, v]) => {
  const names = [...k.matchAll(/\{(\w)\}/g)].map((m) => m[1]);
  const rx = new RegExp("^" + k.split(/\{\w\}/).map(esc).join("(.+?)") + "$", "s");
  return { rx, names, v };
});

export const translateText = (raw) => {
  const s = raw.replace(/\s+/g, " ").trim();
  if (!s) return null;
  let out = EN[s];
  if (!out) {
    for (const p of PATTERNS) {
      const m = s.match(p.rx);
      if (m) { out = p.names.reduce((acc, n, i) => acc.replace(`{${n}}`, translateText(m[i + 1]) || m[i + 1]), p.v); break; }
    }
  }
  if (!out || out === s) return null;
  const lead = raw.match(/^\s*/)[0] ? " " : "";
  const trail = raw.match(/\s*$/)[0] ? " " : "";
  return lead + out + trail;
};

const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = "script,style,textarea,input,[contenteditable],[data-no-tr]";
const orig = new Map();

const fixText = (n) => {
  if (n.parentElement?.closest(SKIP)) return;
  const cur = orig.get(n);
  if (cur && n.nodeValue === cur.out) return;
  const out = translateText(n.nodeValue);
  if (out) { orig.set(n, { src: n.nodeValue, out }); n.nodeValue = out; }
};
const fixAttrs = (el) => {
  ATTRS.forEach((a) => {
    const v = el.getAttribute?.(a);
    if (!v || el.dataset[`tr${a.replace("-", "")}`] === v) return;
    const out = translateText(v);
    if (out) { el.dataset[`trsrc${a.replace("-", "")}`] = v; el.dataset[`tr${a.replace("-", "")}`] = out.trim(); el.setAttribute(a, out.trim()); }
  });
};
const walk = (root) => {
  if (root.nodeType === 3) { fixText(root); return; }
  if (root.nodeType !== 1) return;
  fixAttrs(root);
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = w.nextNode(); n; n = w.nextNode()) { n.nodeType === 3 ? fixText(n) : fixAttrs(n); }
};
const revert = () => {
  orig.forEach(({ src, out }, n) => { if (n.isConnected && n.nodeValue === out) n.nodeValue = src; });
  orig.clear();
  document.querySelectorAll("[data-trsrcplaceholder],[data-trsrctitle],[data-trsrcarialabel]").forEach((el) => {
    ATTRS.forEach((a) => {
      const k = a.replace("-", "");
      if (el.dataset[`trsrc${k}`]) { el.setAttribute(a, el.dataset[`trsrc${k}`]); delete el.dataset[`trsrc${k}`]; delete el.dataset[`tr${k}`]; }
    });
  });
};

// Translates any leftover Indonesian UI text in the DOM while staff use English.
export function useAutoTranslate() {
  const { lang } = useLang();
  useEffect(() => {
    if (lang !== "en") { revert(); return undefined; }
    walk(document.body);
    const obs = new MutationObserver((list) => {
      list.forEach((m) => {
        if (m.type === "characterData") fixText(m.target);
        else if (m.type === "attributes") fixAttrs(m.target);
        else m.addedNodes.forEach(walk);
      });
    });
    obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    const { confirm, alert } = window;
    window.confirm = (msg) => confirm.call(window, translateText(String(msg))?.trim() || msg);
    window.alert = (msg) => alert.call(window, translateText(String(msg))?.trim() || msg);
    return () => { obs.disconnect(); window.confirm = confirm; window.alert = alert; revert(); };
  }, [lang]);
}
